/* Xiaomi 2026 · Today / KPI dashboard
 *
 * Sprint 1 minimal: pulls seed-status + lists 5 lifecycles with
 * deep-links to the board view. Real KPI tiles land in Sprint 3.
 * Sprint 2A · Day 1.5: cross-entity activity stream tile.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import XiaomiTodayTile from '../components/XiaomiTodayTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function XiaomiDashboard() {
  const [status, setStatus] = useState(null);
  const [activity, setActivity] = useState({ rows: [], loading: true });
  const [dqSummary, setDqSummary] = useState(null);
  const [sparkline, setSparkline] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem('token');
    const h = { headers: { Authorization: `Bearer ${t}` } };
    Promise.all([
      axios.get(`${API}/xiaomi/admin/seed-status`, h).then((r) => r.data),
      axios.get(`${API}/xiaomi/admin/activity-stream?limit=20`, h)
        .then((r) => r.data).catch(() => ({ rows: [] })),
      axios.get(`${API}/xiaomi/dealers/dq-summary`, h)
        .then((r) => r.data).catch(() => null),
      axios.get(`${API}/xiaomi/benefits/readiness-sparkline`, h)
        .then((r) => r.data).catch(() => null),
      axios.get(`${API}/xiaomi/admin/escalation-fallback-summary`, h)
        .then((r) => r.data).catch(() => null),
      axios.get(`${API}/xiaomi/feedback/summary`, h)
        .then((r) => r.data).catch(() => null),
    ])
      .then(([s, a, dq, sp, fb, fbk]) => {
        setStatus(s);
        setActivity({ rows: a.rows || [], loading: false });
        setDqSummary(dq);
        setSparkline(sp);
        setFallback(fb);
        setFeedbackSummary(fbk);
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Status unavailable'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-soft text-sm">Loading…</div>;
  }
  const counts = status?.counts || {};

  return (
    <div data-testid="xiaomi-dashboard">
      <h1 style={{ fontFamily: 'Fraunces, serif' }}>Today</h1>
      <p className="text-soft text-sm mb-2">
        Xiaomi ONE 2026 · 284 Platinum Partners · 17 benefits · 7 months
      </p>

      {status?.test_data_counts && (
        (status.test_data_counts.xiaomi_benefit_instances > 0
         || status.test_data_counts.xiaomi_workflow_events > 0) && (
          <div
            className="x-card-soft px-3 py-2 mb-6 text-[11px] flex items-center gap-2"
            data-testid="xiaomi-dashboard-test-data-badge"
            style={{ background: '#fff3e0', borderColor: '#f7d9aa', color: '#8a5a1c' }}
          >
            <span style={{ fontSize: 14 }}>⚠</span>
            <span>
              <strong>Pre-launch fixtures hidden:</strong>{' '}
              {status.test_data_counts.xiaomi_benefit_instances} test instance(s) ·{' '}
              {status.test_data_counts.xiaomi_workflow_events} test event(s). Real
              programme starts at zero.
            </span>
          </div>
        )
      )}

      {err && (
        <div className="x-card-soft px-4 py-3 mb-6 text-sm">
          {err}
        </div>
      )}

      {/* Today · Xiaomi 2026 — same tile that lives on the CEO dashboard,
          now embedded here so Ayush / Kavita / Nisha / Tanisha and every
          other Xiaomi-access user see the live partner utilisation +
          ScaleBoard + Zoho activity for the day the moment they open
          /xiaomi.  embedded=true hides the redundant "Open Xiaomi" button
          since we're already on the dashboard. */}
      <XiaomiTodayTile embedded />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
        {[
          ['Dealers',      counts.xiaomi_dealers || 0,           '👥'],
          ['Benefits',     counts.xiaomi_benefits || 0,          '🎁'],
          ['Active instances', counts.xiaomi_benefit_instances || 0, '⚡'],
          ['Events logged',counts.xiaomi_workflow_events || 0,    '📋'],
          ['Phone tasks',  counts.xiaomi_team_tasks || 0,         '📞'],
          ['DQ issues',    counts.xiaomi_dq_issues || 0,          '⚠️'],
        ].map(([label, val, icon]) => (
          <div
            key={label}
            className="x-card p-4"
            data-testid={`xiaomi-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-soft">
              <span>{icon}</span>
              {label}
            </div>
            <div className="num-display">{val}</div>
          </div>
        ))}
      </div>

      {sparkline?.rows?.length > 0 && (
        <section className="mb-8" data-testid="xiaomi-benefit-sparkline">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mb-0">
                Launch readiness
              </h2>
              <p className="text-soft text-[12px]">
                {sparkline.rows.length} benefits · {sparkline.summary.green} green
                · {sparkline.summary.amber} amber · {sparkline.summary.red} red
                · {sparkline.total_partners} partners
              </p>
            </div>
            <Link to="/xiaomi/admin/benefits"
                  className="text-[11px] text-soft hover:text-ink underline">
              full benefits →
            </Link>
          </div>
          <div className="x-card p-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              {sparkline.rows.map((r) => (
                <SparklineDot key={r.benefit_id} row={r} />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-3 text-[10.5px] text-soft">
              <span><DotInline color="#128c7e" /> ≥95% ready</span>
              <span><DotInline color="#d98a1f" /> 60–94% ready</span>
              <span><DotInline color="#c0492f" /> &lt;60% ready</span>
              <span className="ml-auto">
                Hover each dot for benefit · partners ready
              </span>
            </div>
          </div>
        </section>
      )}

      {feedbackSummary && (
        <section className="mb-8" data-testid="xiaomi-feedback-summary">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mb-0">
                Partner feedback
              </h2>
              <p className="text-soft text-[12px]">
                {feedbackSummary.total === 0
                  ? 'No feedback captured yet · LSMs + WhatsApp inbound feed this once benefits start firing 1 June.'
                  : `${feedbackSummary.total} entries · ${feedbackSummary.by_sentiment.positive} positive · ${feedbackSummary.by_sentiment.neutral} neutral · ${feedbackSummary.by_sentiment.negative} negative`}
              </p>
            </div>
            <span className="text-[11px] text-soft">
              Cloudinary-stored photos + videos · LSM + WA paths
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="x-card p-4" data-testid="feedback-tile-total">
              <div className="text-[11px] uppercase tracking-wider text-soft">Total entries</div>
              <div className="text-3xl font-bold mt-1 text-[#15110d]">{feedbackSummary.total}</div>
            </div>
            <div className="x-card p-4" data-testid="feedback-tile-positive"
                 style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
              <div className="text-[11px] uppercase tracking-wider" style={{ color: '#15803d' }}>
                😊 Positive
              </div>
              <div className="text-3xl font-bold mt-1" style={{ color: '#15803d' }}>
                {feedbackSummary.by_sentiment.positive}
              </div>
            </div>
            <div className="x-card p-4" data-testid="feedback-tile-neutral"
                 style={{ background: '#fafaf9', borderColor: '#e7e5e4' }}>
              <div className="text-[11px] uppercase tracking-wider text-soft">😐 Neutral</div>
              <div className="text-3xl font-bold mt-1 text-[#15110d]">
                {feedbackSummary.by_sentiment.neutral}
              </div>
            </div>
            <div className="x-card p-4" data-testid="feedback-tile-negative"
                 style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
              <div className="text-[11px] uppercase tracking-wider" style={{ color: '#b91c1c' }}>
                😔 Needs follow-up
              </div>
              <div className="text-3xl font-bold mt-1" style={{ color: '#b91c1c' }}>
                {feedbackSummary.by_sentiment.negative}
              </div>
            </div>
          </div>
          {(feedbackSummary.recent_negative || []).length > 0 && (
            <div className="x-card p-4 mt-3" data-testid="feedback-recent-negative-list">
              <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
                Recent negative feedback · top {feedbackSummary.recent_negative.length}
              </div>
              <ul className="text-[13px] space-y-2">
                {feedbackSummary.recent_negative.map((r) => (
                  <li key={r.feedback_id} className="border-l-2 border-[#b91c1c] pl-3">
                    <span className="font-mono text-[11px] text-soft">{r.dealer_id}</span>
                    {' · '}
                    <span className="text-[#15110d]">{r.benefit_id}</span>
                    {' · '}
                    <span className="text-[#374151]">{(r.text || '').slice(0, 120)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mb-3">Journey boards</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-10">
        {[
          { key: 'gift_15_step',                label: 'Gift journeys',        icon: '🎁',
            sub: 'Birthday · Onboarding · Welcome Cert · Spouse Kit · Year-End' },
          { key: 'service_redemption_15_step',  label: 'Service journeys',     icon: '📸',
            sub: 'Family Photoshoot · Concierge Service' },
          { key: 'event_invite_15_step',        label: 'Event journeys',       icon: '🍽️',
            sub: 'Dinner · Brunch · Day Out · Cultural Showcase' },
          { key: 'staycation_15_step',          label: 'Staycation journeys', icon: '✈️',
            sub: 'Anniversary · Domestic Holiday' },
          { key: 'zsm_gift_15_step',            label: 'ZSM gift',             icon: '👔',
            sub: 'Leaders ZSM Gift · ZSM-approved dispatch' },
        ].map((j) => (
          <Link
            key={j.key}
            to={`/xiaomi/board/${j.key}`}
            className="x-card p-4 hover:shadow-md transition flex items-center gap-3"
            data-testid={`xiaomi-board-link-${j.key}`}
          >
            <div className="text-2xl">{j.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink">{j.label}</div>
              <div className="text-xs text-soft truncate">{j.sub}</div>
            </div>
            <div className="text-xl text-soft">→</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Activity stream — awareness tile · last 20 system edits */}
        <div
          className="x-card p-4 lg:col-span-2"
          data-testid="xiaomi-activity-stream"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 style={{ fontFamily: 'Fraunces, serif' }} className="text-base">
              Recent system activity
            </h3>
            <Link to="/xiaomi/partners"
                  className="text-[11px] text-soft hover:text-ink underline">
              full audit →
            </Link>
          </div>
          {activity.loading && <div className="text-soft text-xs">Loading…</div>}
          {!activity.loading && activity.rows.length === 0 && (
            <div className="text-soft text-xs italic py-6 text-center">
              No edits yet · system is quiet
            </div>
          )}
          {!activity.loading && activity.rows.length > 0 && (
            <div className="space-y-1.5 text-[12px]">
              {activity.rows.slice(0, 12).map((r, i) => (
                <div
                  key={i}
                  className="flex items-baseline gap-2 py-1 border-b border-line-soft last:border-0"
                  data-testid={`xiaomi-activity-row-${i}`}
                >
                  <span className="text-soft text-[10.5px]" style={{ minWidth: 100 }}>
                    {formatRelativeTime(r.ts)}
                  </span>
                  <span className="text-ink font-medium truncate" style={{ maxWidth: 160 }}>
                    {r.entity_label}
                  </span>
                  <span className="text-soft text-[10.5px]">·</span>
                  <span className="font-mono text-[11px]" style={{ color: '#d98a1f' }}>
                    {r.field}
                  </span>
                  <span className="text-soft text-[10.5px]">·</span>
                  <code className="text-[10.5px] text-soft">{stringifyShort(r.before)}</code>
                  <span className="text-soft text-[10.5px]">→</span>
                  <code className="text-[10.5px]" style={{ color: '#ff6900' }}>
                    {stringifyShort(r.after)}
                  </code>
                  <span className="ml-auto text-[10.5px] text-soft truncate" style={{ maxWidth: 160 }}>
                    {(r.actor_email || '').split('@')[0]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DQ summary — quick action card */}
        <div
          className="x-card p-4 flex flex-col"
          data-testid="xiaomi-dq-summary-tile"
          style={{ background: '#fff3e0', borderColor: '#f7d9aa' }}
        >
          <h3 style={{ fontFamily: 'Fraunces, serif', color: '#8a5a1c' }} className="text-base mb-2">
            Data quality
          </h3>
          {dqSummary ? (
            <>
              <div className="num-display mb-1" style={{ color: '#d98a1f', fontSize: 38 }}>
                {dqSummary.with_gaps}
              </div>
              <div className="text-[11.5px]" style={{ color: '#8a5a1c' }}>
                of {dqSummary.total_partners} partners have data gaps.
              </div>
              {dqSummary.by_missing_field && Object.keys(dqSummary.by_missing_field).length > 0 && (
                <div className="mt-3 space-y-0.5 text-[11px]" style={{ color: '#8a5a1c' }}>
                  {Object.entries(dqSummary.by_missing_field).slice(0, 5).map(([f, n]) => (
                    <div key={f}>
                      <strong>{n}</strong> missing <code>{f}</code>
                    </div>
                  ))}
                </div>
              )}
              <Link
                to="/xiaomi/partners"
                className="mt-auto pt-3 text-[11.5px] underline"
                style={{ color: '#d98a1f' }}
                data-testid="xiaomi-dq-summary-cta"
              >
                Open Partners → DQ CSV for RSMs
              </Link>
              {fallback && fallback.partners_falling_back_for_zsm > 0 && (
                <div
                  className="mt-3 pt-3 border-t border-[#f7d9aa] text-[11px]"
                  style={{ color: '#8a5a1c' }}
                  data-testid="xiaomi-fallback-chip"
                >
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 12 }}>⇗</span>
                    <strong>{fallback.partners_falling_back_for_zsm}</strong>
                    <span>escalations routing to fallback</span>
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: '#a06a26' }}>
                    → {fallback.fallback_target_name} · self-healing as
                    ZSM data is filled in
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-soft text-xs">Loading…</div>
          )}
        </div>
      </div>
    </div>
  );
}


function formatRelativeTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function stringifyShort(v) {
  if (v === null || v === undefined) return '∅';
  if (v === '') return '""';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > 28 ? s.slice(0, 25) + '…' : s;
}


function SparklineDot({ row }) {
  const colorMap = { green: '#128c7e', amber: '#d98a1f', red: '#c0492f' };
  const bg = colorMap[row.color] || '#6b6155';
  const tooltip =
    `${row.ordinal}. ${row.name}\n` +
    `${row.ready} of ${row.total} ready (${row.pct}%)\n` +
    (row.required_fields?.length
      ? `Blocked by: ${row.required_fields.join(', ')}`
      : 'No required fields');
  return (
    <div
      className="relative group"
      data-testid={`xiaomi-sparkline-dot-${row.benefit_id}`}
      title={tooltip}
    >
      <div
        className="rounded-full transition-transform hover:scale-125 cursor-pointer"
        style={{
          width: 28, height: 28, background: bg,
          border: `2px solid ${row.color === 'green' ? '#a3e2cf' : row.color === 'amber' ? '#f7d9aa' : '#f0c4b8'}`,
        }}
      />
      <span
        className="absolute left-1/2 -translate-x-1/2 -bottom-4 text-[9px] text-soft font-mono"
        style={{ whiteSpace: 'nowrap' }}
      >
        {row.ordinal}
      </span>
    </div>
  );
}

function DotInline({ color }) {
  return (
    <span
      style={{
        display: 'inline-block', width: 8, height: 8,
        borderRadius: '50%', background: color, marginRight: 4,
      }}
    />
  );
}
